package modals

import analyticsdashboard.ActiveHistoryPathSearchFieldValuesStore
import apiclient.FormationClient
import apiclient.customfields.CategoryReference
import apiclient.customfields.FieldValue
import apiclient.customfields.parseFieldValues
import apiclient.geoobjects.AddFieldValue
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectChanges
import apiclient.geoobjects.ReplaceFieldValue
import apiclient.geoobjects.applyObjectChanges
import auth.CurrentWorkspaceStore
import data.heatmaplayer.ActiveHeatmapLayerDefinitionStore
import data.keywordlayer.ActiveKeywordLayerDefinitionStore
import data.keywordlayer.MapLayerTypeSwitchStore
import data.objects.ActiveObjectFieldValuesStore
import data.objects.ActiveObjectStore
import data.objects.CurrentActiveFieldValueStore
import data.objects.WorkspaceCategoryNamespacesStore
import dev.fritz2.components.stackUp
import dev.fritz2.core.RenderContext
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.Store
import dev.fritz2.core.invoke
import dev.fritz2.core.storeOf
import dev.fritz2.core.values
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.map
import kotlinx.datetime.Instant
import localization.TL
import map.views.workplacetools.ActiveArchetypeSearchFieldValuesStore
import model.L
import overlays.BusyStore
import search.global.ActiveSearchFieldValuesStore
import search.nestedObjects.ActiveNestedSearchFieldValuesStore
import theme.FormationUIIcons
import twcomponents.selectorIndicator
import twcomponents.twPrimaryButtonSmall
import twcomponents.twRowOfJustifyBetween
import twcomponents.twSimpleModalWithCloseHeader
import utils.handleFunctions
import utils.respectFeatureFlags
import utils.toKeywordTag
import webcomponents.KeywordTagActionType
import webcomponents.KeywordTagType
import webcomponents.genericInput
import webcomponents.keywordTag
import webcomponents.twEllipseText

fun RenderContext.twFieldValueModal(
    toggleShowModalStore: Store<Boolean>,
    tagType: KeywordTagType? = null,
) {
    withKoin {
        val workspaceCategoryNamespacesStore: WorkspaceCategoryNamespacesStore = get()
        val activeObjectFieldValuesStore: ActiveObjectFieldValuesStore = get()
        val activeSearchFieldValuesStore: ActiveSearchFieldValuesStore = get()
        val activeArchetypeSearchFieldValuesStore: ActiveArchetypeSearchFieldValuesStore = get()
        val activeNestedSearchFieldValuesStore: ActiveNestedSearchFieldValuesStore = get()
        val activeHistoryPathSearchFieldValuesStore: ActiveHistoryPathSearchFieldValuesStore = get()
        val activeHeatmapLayerDefinitionStore: ActiveHeatmapLayerDefinitionStore = get()
        val activeKeywordLayerDefinitionsStore: ActiveKeywordLayerDefinitionStore = get()
        val mapLayerTypeSwitchStore: MapLayerTypeSwitchStore = get()
        val currentActiveFieldValueStore: CurrentActiveFieldValueStore = get()

        val saveActiveFieldValueHandler: SimpleHandler<FieldValue?>

        // if keywordTagType is not set by param, then try to get it from mapLayerTypeSwitch
        val type = tagType ?: mapLayerTypeSwitchStore.keyWordTagType

        saveActiveFieldValueHandler = when (type) {
            KeywordTagType.ObjectTag -> SimpleHandler { data, _ ->
                data handledBy { fieldValue ->
                    if (fieldValue != null) {
                        withKoin {
                            val busyStore: BusyStore = get()
                            val client: FormationClient = get()
                            val activeObjectStore: ActiveObjectStore = get()
                            val currentWorkspaceStore: CurrentWorkspaceStore = get()
                            val tagsSub = activeObjectStore.map(GeoObjectDetails.L.tags)
                            val activeFieldValueFieldNames = activeObjectStore.current.tags.respectFeatureFlags().let { tags ->
                                currentWorkspaceStore.current?.fieldDefinitions?.let { definitions ->
                                    tags.parseFieldValues(definitions).map { it.field }
                                }
                            }.orEmpty()

                            busyStore.handleApiCall(
                                supplier = {
                                    client.applyObjectChanges(
                                        ObjectChanges(
                                            activeObjectStore.current.id,
                                            if (fieldValue.field in activeFieldValueFieldNames) {
                                                // change fieldValue
                                                ReplaceFieldValue(fieldValue)
                                            } else {
                                                // add fieldValue
                                                AddFieldValue(fieldValue)
                                            },
                                        ),
                                    )
                                },
                                processResult = { geoObjects ->
                                    val updatedActiveObj = geoObjects.firstOrNull { it.id == activeObjectStore.current.id }
                                    updatedActiveObj?.let { updated ->
                                        // update activeObject with new fieldValues
                                        tagsSub.update(updated.tags)
                                    }
                                },
                                withBusyState = true,
                            )
                        }
                    }
                }
            }

            KeywordTagType.SearchTag -> activeSearchFieldValuesStore.addOrChangeFieldValue
            KeywordTagType.AnalyticsPathViewTag -> activeHistoryPathSearchFieldValuesStore.addOrChangeFieldValue
            KeywordTagType.ArchetypeTag -> activeArchetypeSearchFieldValuesStore.addOrChangeFieldValue
            KeywordTagType.NestedObjectTag -> activeNestedSearchFieldValuesStore.addOrChangeFieldValue
            KeywordTagType.HeatmapLayerTag -> activeHeatmapLayerDefinitionStore.addOrChangeFieldValue
            KeywordTagType.KeywordLayerTag -> activeKeywordLayerDefinitionsStore.addOrChangeFieldValue
        }

        twSimpleModalWithCloseHeader(
            title = TL.KeywordTags.SELECT_A_VALUE,
            icon = FormationUIIcons.Select,
            toggleStore = toggleShowModalStore,
        ) { closeModal, _, optionalCloseHandlers ->
            div(
                baseClass = "flex flex-col gap-3 w-full md:min-w-96 overflow-y-auto p-2",
                id = "meeting-dateTime-modal",
            ) {

                val valueValidationMessageStore = storeOf("")

                console.log("CardManageFieldValueTag, for:", type.name)
                @Suppress("UNCHECKED_CAST")
                class ValueStore<T>(fieldValue: FieldValue, value: T) : RootStore<T>(
                    initialData = value,
                    job = Job(),
                ) {
                    val updateValue = handle<String> { current, stringValue ->
                        when {
                            fieldValue is FieldValue.DoubleValue && value is Double -> {
                                try {
                                    val double = (stringValue.ifBlank { "0.0" }).toDouble()
                                    valueValidationMessageStore.update("")
                                    currentActiveFieldValueStore.update(fieldValue.copy(value = double))
                                    double as T
                                } catch (e: NumberFormatException) {
                                    console.log("Value is no Double!", e.message)
                                    valueValidationMessageStore.update("Please choose a number like: 87 or -35.204")
                                    0.0 as T
                                }
                            }

                            fieldValue is FieldValue.LongValue && value is Long -> {
                                try {
                                    val long = (stringValue.ifBlank { "0" }).toLong()
                                    valueValidationMessageStore.update("")
                                    currentActiveFieldValueStore.update(fieldValue.copy(value = long))
                                    long as T
                                } catch (e: NumberFormatException) {
                                    console.log("Value is no Long!", e.message)
                                    valueValidationMessageStore.update("Please choose a number between ${Long.MIN_VALUE} and ${Long.MAX_VALUE}")
                                    0 as T
                                }
                            }

                            fieldValue is FieldValue.InstantValue && value is Instant -> {
                                try {
                                    val instant = Instant.parse(stringValue)
                                    valueValidationMessageStore.update("")
                                    currentActiveFieldValueStore.update(fieldValue.copy(value = instant))
                                    instant as T
                                } catch (e: Error) {
                                    console.log("Value is no Instant:", stringValue)
                                    valueValidationMessageStore.update("Value is not a valid date!")
                                    current
                                }
                            }

                            fieldValue is FieldValue.StringValue && value is String -> {
                                valueValidationMessageStore.update("")
                                currentActiveFieldValueStore.update(fieldValue.copy(value = stringValue))
                                @Suppress("UNCHECKED_CAST")
                                stringValue as T
                            }

                            else -> {
                                current
                            }
                        }
                    }
                }

                // List of fieldValue tag options or input to change value
                div("flex flex-col w-full max-h-max gap-2 overflow-y-auto") {
                    // Re-render tag to display current selected/typed value of the fieldValue
                    currentActiveFieldValueStore.data.render { fieldValue ->
                        if (fieldValue != null) {
                            div("flex w-full items-start mb-4") {
                                keywordTag(
                                    tag = fieldValue.toKeywordTag(KeywordTagActionType.DefaultFieldValue),
                                    keywordTagType = type,
                                )
                            }
                        }
                    }

                    // Re-render the value, if it's a CategoryValue
                    currentActiveFieldValueStore.data.render { fieldValue ->
                        if (fieldValue != null && fieldValue is FieldValue.CategoryValue) {
                            stackUp(
                                {
                                    width { full }
                                    alignItems { center }
                                },
                            ) {
                                spacing { normal }
                                items {
                                    workspaceCategoryNamespacesStore.data.render { wsCategoryNamespaces ->
                                        val categoryNamespace =
                                            wsCategoryNamespaces?.firstOrNull { it.name == fieldValue.value.namespace }
                                        categoryNamespace?.categories?.forEach { categoryDefinition ->
                                            div("flex flex-row w-full h-max items-center justify-between cursor-pointer hover:bg-gray-100 p-2 gap-2 rounded-xl") {
                                                twEllipseText("text-sm font-bold") {
                                                    +categoryDefinition.title
                                                }
                                                selectorIndicator(categoryDefinition.name == fieldValue.value.categoryName)

                                                clicks handledBy {
                                                    val newVal = fieldValue.copy(
                                                        value = CategoryReference(
                                                            namespace = categoryNamespace.name,
                                                            categoryName = categoryDefinition.name,
                                                        ),
                                                    )
                                                    currentActiveFieldValueStore.update(newVal)
                                                    saveActiveFieldValueHandler(newVal)
                                                    optionalCloseHandlers?.let { handlers ->
                                                        handlers.forEach {
                                                            it.invoke()
                                                        }
                                                    }
                                                    closeModal()
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    // Do not re-render input fields for other fieldValues
                    if (currentActiveFieldValueStore.current != null) {
                        div("p-2") {
                            when (val fieldValue = currentActiveFieldValueStore.current) {
                                is FieldValue.DoubleValue -> {
                                    val doubleValueStore = ValueStore(fieldValue, fieldValue.value)
                                    // TODO replace with tailwind styles inputField
                                    twRowOfJustifyBetween {
                                        genericInput(
                                            value = doubleValueStore.data.map { it.toString() },
                                            enterKeyHandlers = listOf(
                                                handleFunctions {
                                                    optionalCloseHandlers?.forEach { handler ->
                                                        handler()
                                                    }
                                                    saveActiveFieldValueHandler(fieldValue.copy(value = doubleValueStore.current))
                                                    closeModal()
                                                },
                                            ),
                                        ) {
                                            inputs.values() handledBy doubleValueStore.updateValue
                                        }
                                        twPrimaryButtonSmall(TL.General.SET) {
                                            clicks handledBy {
                                                optionalCloseHandlers?.forEach { handler ->
                                                    handler()
                                                }
                                                saveActiveFieldValueHandler(fieldValue.copy(value = doubleValueStore.current))
                                                closeModal()
                                            }
                                        }
                                    }
                                }

                                is FieldValue.InstantValue -> {
                                    val instantValueStore = ValueStore(fieldValue, fieldValue.value)
                                    // TODO add date time picker here
                                }

                                is FieldValue.LongValue -> {
                                    val longValueStore = ValueStore(fieldValue, fieldValue.value)
                                    // TODO replace with tailwind styles inputField
                                    twRowOfJustifyBetween {
                                        genericInput(
                                            value = longValueStore.data.map { it.toString() },
                                            enterKeyHandlers = listOf(
                                                handleFunctions {
                                                    optionalCloseHandlers?.forEach { handler ->
                                                        handler()
                                                    }
                                                    saveActiveFieldValueHandler(fieldValue.copy(value = longValueStore.current))
                                                    closeModal()
                                                },
                                            ),
                                        ) {
                                            inputs.values() handledBy longValueStore.updateValue
                                        }
                                        twPrimaryButtonSmall(TL.General.SET) {
                                            clicks handledBy {
                                                optionalCloseHandlers?.forEach { handler ->
                                                    handler()
                                                }
                                                saveActiveFieldValueHandler(fieldValue.copy(value = longValueStore.current))
                                                closeModal()
                                            }
                                        }
                                    }
                                }

                                is FieldValue.StringValue -> {
                                    val stringValueStore = ValueStore(fieldValue, fieldValue.value)
                                    // TODO replace with tailwind styles inputField

                                    twRowOfJustifyBetween {
                                        genericInput(
                                            value = stringValueStore.data,
                                            enterKeyHandlers = listOf(
                                                handleFunctions {
                                                    optionalCloseHandlers?.forEach { handler ->
                                                        handler()
                                                    }
                                                    saveActiveFieldValueHandler(fieldValue.copy(value = stringValueStore.current))
                                                    closeModal()
                                                },
                                            ),
                                        ) {
                                            inputs.values() handledBy stringValueStore.updateValue
                                        }
                                        twPrimaryButtonSmall(TL.General.SET) {
                                            clicks handledBy {
                                                optionalCloseHandlers?.forEach { handler ->
                                                    handler()
                                                }
                                                saveActiveFieldValueHandler(fieldValue.copy(value = stringValueStore.current))
                                                closeModal()
                                            }
                                        }
                                    }
                                }

                                is FieldValue.EmptyValue -> TODO()
                                else -> {}
                            }
                        }
                    }
                    // validation message
                    div("flex w-96 grow max-w-full") { // min-h-6
                        valueValidationMessageStore.data.render { message ->
                            if (message.isNotBlank()) {
                                p("text-xs text-red-500 bg-red-100 rounded-xl p-2 my-2") {
                                    +message
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
